import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from 'components/Modals/presets/BasicModal';
import { isEqual } from 'lodash';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import ConfirmNavigationModal from 'components/Modals/presets/ConfirmNavigation';
import useModal from 'components/Modals/useModal';
import {
  StyledNotice,
  StyledOutlineInputControl,
  StyledTextTemplaterWrapper,
} from '../styles';
import TextTemplater from 'components/Kizen/TextTemplater';
import { useAsync } from 'react-use';
import CustomObjectsService from 'services/CustomObjectsService';
import Loader from 'components/Kizen/Loader';
import { DEFAULT_FIELD_NAMES, FIELD_TYPES } from 'utility/constants';
import { toastVariant, useToast } from 'components/ToastProvider';
import { getErrorMessage } from 'hooks/useErrors';
import { getOriginalError } from 'services/AxiosService';
import Validation from 'components/Inputs/Validation';
import { useFlashTransition } from 'hooks/useFlashState';

export const EditNameTemplateModal = ({
  data,
  customObject,
  onConfirm,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const [stagedData, setStagedData] = useState(data);

  const templaterRef = useRef();
  const [message, showMessage, flashErrorMessage] = useFlashTransition();

  const isDirty = useMemo(() => {
    return !isEqual(data, stagedData);
  }, [stagedData, data]);

  const [confirmationModalProps, , { show: showConfirmationModal }] = useModal({
    handleSubmit: modalProps.onHide,
  });

  const onHide = useCallback(() => {
    if (!isDirty) {
      modalProps.onHide();
    } else {
      showConfirmationModal();
    }
  }, [modalProps, isDirty, showConfirmationModal]);

  const onSubmit = useCallback(async () => {
    try {
      await CustomObjectsService.updateCustomObject({
        customObject,
        updatedCustomObjectValues: {
          entityDisplayNamePatternText: stagedData.content,
          entityDisplayNamePatternHtml: stagedData.htmlContent,
        },
      });
      onConfirm(stagedData);
    } catch (err) {
      const axiosError = getOriginalError(err);

      if (
        axiosError?.entity_display_name_pattern_html ||
        axiosError?.entity_display_name_pattern_text
      ) {
        flashErrorMessage(
          axiosError.entity_display_name_pattern_html ||
            axiosError.entity_display_name_pattern_text
        );
      } else {
        showToast({
          variant: toastVariant.FAILURE,
          message:
            getErrorMessage(getOriginalError(err)) ||
            t('Failed to update display name template'),
        });
      }
    }
  }, [stagedData, onConfirm, customObject, showToast, flashErrorMessage, t]);

  const { value: mergeFields = [], loading } = useAsync(async () => {
    const { categorizedFields } =
      await CustomObjectsService.getCategorizedModelFields(customObject.id, {
        settingsSearch: true,
      });

    return categorizedFields.reduce((caregoriesAcc, { name, fields, id }) => {
      const items = fields.reduce((fieldsAcc, field) => {
        if (
          [FIELD_TYPES.Text.type, FIELD_TYPES.Email.type].includes(
            field.fieldType
          ) &&
          field.name !== DEFAULT_FIELD_NAMES.displayName
        ) {
          fieldsAcc.push({
            label: `${field.displayName} (${field.name})`,
            value: `{{ ${field.name} }}`,
            relationship: field.name,
            fieldType: field.fieldType,
          });
        }
        return fieldsAcc;
      }, []);

      if (items.length) {
        caregoriesAcc.push({
          key: id,
          label: name,
          items,
        });
      }
      return caregoriesAcc;
    }, []);
  }, []);

  return (
    <>
      <BasicModal
        heading={t('Edit Name Template')}
        size="medium"
        enforceFocus={false}
        disabled={false}
        {...modalProps}
        onHide={onHide}
        onConfirm={onSubmit}
      >
        <Loader loading={loading}>
          <StyledNotice>
            {t(
              'Updating the display name template will change how this record appears in related object fields, type-aheads, and timeline entries. Upon update, all records will be updated asynchronously.'
            )}
          </StyledNotice>
          <StyledTextTemplaterWrapper ref={templaterRef}>
            <TextTemplater
              inModal
              appendMergeFieldExtraSpace={false}
              title={
                <StyledOutlineInputControl label={t('Display Name Template')} />
              }
              defaultValue={stagedData.htmlContent}
              textareaOptions={{
                placeholder: t('Enter Template'),
                enableResize: false,
                borderHoverColor: 'var(--color-button-secondary-hover)',
              }}
              customize={{
                mergeFields,
                autoSuggestions: true,
                wrapperClassName: 'templater',
                showCountComponent: false,
                disableFocus: true,
              }}
              onSave={setStagedData}
              oneLineLayout
              dataQA="edit-name-template"
            />
            <Validation
              target={templaterRef.current}
              inModal
              message={message}
              showMessage={showMessage}
            />
          </StyledTextTemplaterWrapper>
        </Loader>
      </BasicModal>
      <ConfirmationModal
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        actionBtnColor="red"
        {...confirmationModalProps}
      >
        {t('Unsaved changes will be lost, would you like to continue?')}
      </ConfirmationModal>
      <ConfirmNavigationModal when={isDirty} />
    </>
  );
};
